// ScrollLock utility prevents background page from scrolling when the modal is open
import ScrollLock from '../../components/scroll-lock/scroll-lock';
import './modal.scss';

class Modal {
    /**
     * Creates a modal controller bound to a trigger element.
     *
     * Expected markup:
     * - Trigger element has either [data-modal]="<targetId>" or a class prefixed with options.targetClassPrefix
     * - Modal container element has id="<targetId>"
     * - Close button inside modal marked with [data-modal-close-btn]
     *
     * @param {HTMLElement} element - The trigger element that opens the modal.
     * @param {Object} options - Optional settings to customize attributes/behavior.
     */
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-modal',
            activeAttr: 'data-modal-active',
            modalAttr: 'data-modaltarget',
            closeButtonAttr: 'data-modal-close-btn',
            onClosed: null,
            scrollerAttr: 'scroller',
            targetClassPrefix: null
        };

        this.settings = Object.assign({}, defaults, options);
        this.$root = element;

        // Properties
        const classDerivedId = this.settings.targetClassPrefix
            ? (Array.from(this.$root.classList)
                .find(c => c.startsWith(this.settings.targetClassPrefix))
                ?.replace(this.settings.targetClassPrefix, ''))
            : null;

        this.modalId = this.$root.getAttribute(this.settings.initAttr) ?? classDerivedId;

        // Elements
        this.$activeModal = null;
        this.$modalTarget = document.getElementById(this.modalId);
        this.$closeButton = this.$modalTarget?.querySelector(`[${this.settings.closeButtonAttr}]`);
        this.$modalScroller = this.$modalTarget?.querySelector(`[${this.settings.modalAttr}="${this.settings.scrollerAttr}"]`);

        this.modalOpen = false;
        this.startedFirstTime = false;

        if (!this.$modalTarget) {
            return;
        }
    }

    /**
     * Initializes event listeners and emits a 'modal.ready' event for external hooks.
     */
    initialize () {
        this.setEvents();

        const modalReady = new CustomEvent('modal.ready', {
            detail: {api: this, modalTarget: this.$modalTarget},
            bubbles: true
        });

        window.dispatchEvent(modalReady);
    }

    /**
     * Opens the modal: emits lifecycle events, manages aria attributes, focuses the close button,
     * and locks scroll if a modal scroller is present.
     */
    openModal () {
        if (!this.startedFirstTime) {
            this.startedFirstTime = true;

            const modalStartedFirstTime = new CustomEvent('modal.started.firstTime', {
                detail: {api: this, modalTarget: this.$modalTarget, modalScroller: this.$modalScroller},
                bubbles: true
            });

            window.dispatchEvent(modalStartedFirstTime);
        }

        const modalOpened = new CustomEvent('modal.opened', {
            detail: {api: this, modalTarget: this.$modalTarget},
            bubbles: true
        });

        window.dispatchEvent(modalOpened);

        this.$root.setAttribute('aria-pressed', 'true');
        this.$activeModal = document.querySelector(`[${this.settings.activeAttr}]`);

        if (this.$activeModal && this.$activeModal.API) {
            this.$activeModal.API.closeModal();
        }

        this.$modalTarget.setAttribute(this.settings.activeAttr, '');
        this.$modalTarget.removeAttribute('inert');
        this.$root.setAttribute('aria-expanded', 'true');

        setTimeout(() => {
            this.$closeButton?.focus();
        }, 100);

        if (this.$modalScroller) {
            ScrollLock.disableScroll();
        }

        this.modalOpen = true;
    }

    /**
     * Closes the modal: restores inert state, unlocks scroll (if needed),
     * updates trigger aria attributes, returns focus to the trigger,
     * and emits a 'modal.closed' event.
     */
    closeModal () {
        if (!this.modalOpen) return;

        this.modalOpen = false;
        this.$modalTarget.removeAttribute(this.settings.activeAttr);
        this.$modalTarget.setAttribute('inert', '');

        if (this.$modalScroller) {
            ScrollLock.enableScroll();
        }

        if (typeof this.settings.onClosed === 'function') {
            this.settings.onClosed(this.$modalTarget);
        }

        this.$root.setAttribute('aria-pressed', 'false');
        this.$root.setAttribute('aria-expanded', 'false');
        this.$root.focus();

        const modelClosed = new CustomEvent('modal.closed', {
            detail: {api: this, modalTarget: this.$modalTarget},
            bubbles: true
        });

        window.dispatchEvent(modelClosed);
    }

    /**
     * Helper to close the modal when clicking outside the modal content.
     * Note: currently not wired globally; can be bound externally if needed.
     *
     * @param {MouseEvent} e
     */
    handleOutsideClick (e) {
        if (this.modalOpen && this.$modalTarget && !this.$modalTarget.contains(e.target)) {
            this.closeModal();
        }
    }

    /**
     * Binds interaction handlers for opening and closing the modal.
     * - Clicking the trigger opens the modal
     * - Clicking or pressing Enter/Space on the close button closes the modal
     */
    setEvents () {
        this.$root.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal();
        });

        this.$closeButton?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.closeModal();
            }
        });
        this.$closeButton?.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeModal();
        });
    }
}

export {Modal};

// Auto-initialization: whenever new content is added, scan for modal triggers
// and wire each to its corresponding modal target by id or class prefix.
window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const targetClassPrefix = 'modal-id-';
        const $$roots = $context.querySelectorAll(
            `[data-modal], [class*="${targetClassPrefix}"]`
        );

        $$roots.forEach($root => {
            const rootAPI = new Modal($root, {targetClassPrefix});
            $root.API = rootAPI;

            if (rootAPI.$modalTarget) {
                rootAPI.$modalTarget.API = rootAPI;
            }

            rootAPI.initialize();
        });
    }
});
